import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown, ArrowUpRight } from 'lucide-react';
import { lineWrap, lineInner, EASE_OUT } from '../lib/anim';
import { scrollToId } from './SmoothScroll';
import projectCover from "../assets/foto profilo.png";

const START = 1.8; // sync with preloader exit

const TitleLine = ({ children, className }) => (
  <span className="mask-line">
    <motion.span variants={lineInner} className={`block ${className || ''}`}>
      {children}
    </motion.span>
  </span>
);


const HERO = {
  title: "hero-title",
  subtitle: "hero-subtitle",
  cta: "hero-cta",
};



export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });

  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '28%']);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '-40%']);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid={HERO.section}
      className="relative flex min-h-screen w-full flex-col justify-end overflow-hidden pb-16 pt-32"
    >
      {/* Ambient light */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_70%_-10%,rgba(255,255,255,0.10),transparent_55%)]" />

      {/* Parallax portrait */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="pointer-events-none absolute right-[4%] top-1/2 hidden h-[62vh] w-[30vw] max-w-[460px] -translate-y-1/2 overflow-hidden rounded-[2px] md:block"
      >
        <motion.img
          initial={{ scale: 1.3, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.92 }}
          transition={{ duration: 1.6, ease: EASE_OUT, delay: START }}
          src={projectCover}
          alt="Portrait of Michele Rago"
          className="h-full w-full object-cover grayscale contrast-[1.05]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
      </motion.div>

      <motion.div
        style={{ y: textY, opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-[1600px] px-6 md:px-12"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: START + 0.1, ease: EASE_OUT }}
          className="mb-8 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.28em] text-muted-foreground"
        >
          <span className="h-px w-10 bg-muted-foreground/50" />
          Independent Designer · Pavia, IT
        </motion.div>

        <motion.h1
          data-testid={HERO.title}
          variants={lineWrap}
          initial="hidden"
          animate="show"
          custom={START}
          className="font-display font-extrabold uppercase leading-[0.85] tracking-tighter text-foreground"
        >
          <TitleLine className="text-[15vw] md:text-[11vw] lg:text-[10vw]">
            Michele
          </TitleLine>
          <TitleLine className="text-[15vw] text-muted-foreground md:text-[11vw] lg:text-[10vw]">
            Rago<span className="text-foreground">.</span>
          </TitleLine>
        </motion.h1>

        <div className="mt-10 flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: START + 0.5, ease: EASE_OUT }}
            className="max-w-md text-base leading-relaxed text-muted-foreground md:text-lg"
          >
            Creo siti web moderni per PMI e professionisti. I miei siti sono veloci, chiari e ottimizzati per Google, progettati per trasformare i visitatori in richieste di preventivo.
            <span className="text-foreground"> Una volta al mese: realizzo siti web gratuitamente. Contattami, il prossimo potresti essere tu!</span>
          </motion.p>

          <motion.button
            data-testid={HERO.cta}
            onClick={() => scrollToId('work')}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: START + 0.6, ease: EASE_OUT }}
            className="group flex items-center gap-3 rounded-full border border-border px-6 py-3.5 font-mono text-xs uppercase tracking-[0.15em] text-foreground transition-colors duration-300 hover:bg-foreground hover:text-background"
          >
            View selected work
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </motion.button>
        </div>
      </motion.div>

      <motion.div
        data-testid={HERO.scrollHint}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: START + 0.9 }}
        className="relative z-10 mx-auto mt-14 flex w-full max-w-[1600px] items-center gap-3 px-6 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground md:px-12"
      >
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ArrowDown className="h-4 w-4" />
        </motion.span>
        Scroll to explore
      </motion.div>
    </section>
  );
}
